import type { AnyBackendMessageStages, BackendMessageStagesToValidated } from '../types';
import { createPlacementMessageBackend } from '../index';
import { defaultMessageBackend } from '../util/message-helpers';

export {
	createPlacement,
	createBackendPlacementRunners,
	createBackendPlacementMessageTemplateRouter,
	createPlacementMessageBackend,
} from '../index';

/**
 * Type-safe factory helpers for message templates installed into backend-only placements.
 *
 * Use this when there is no real PlacementBackend to import from a placement-specific package.
 * The actual placement (with the correct placement ID derived from the folder name) is created
 * at install time via the CLI — this object only provides the message-level factory methods.
 *
 * Methods:
 *   - createMessageBackend(stages): standard factory, pass your own backend stages
 *   - withDefaultMessageBackend(): shorthand for createMessageBackend(defaultMessageBackend)
 *
 * Usage in a message's index.tsx:
 *   import { DefaultHeadlessPlacementBackend } from '@post-office/placement-common-v2';
 *
 *   // Shorthand (recommended for simple backend-only messages):
 *   export const MessageBackend = DefaultHeadlessPlacementBackend.withDefaultMessageBackend();
 *
 *   // Or with custom stages:
 *   import { defaultMessageBackend } from '@post-office/placement-common-v2';
 *   export const MessageBackend = DefaultHeadlessPlacementBackend.createMessageBackend(defaultMessageBackend);
 */
/**
 * Creates a placeholder frontend entry for a headless message installed in a backend+frontend placement.
 *
 * A headless message provides only a MessageBackend — its rendering is handled in-product by the
 * consuming team (e.g. via usePlacementData). This factory creates a no-op FrontendPlacementMessage
 * so the message template ID is still registered in messageFrontends and the placement can track it
 * via headlessMessageTemplateIds.
 *
 * Usage in a generated placement file:
 *   import { createHeadlessMessageFrontend } from '@post-office/placement-common-v2';
 *   const messageFrontends = [
 *     createMessageFrontendMyMessage({ messageTemplateId: 'my-message' }),
 *     createHeadlessMessageFrontend({ messageTemplateId: 'headless-msg' }),
 *   ];
 */
export const createHeadlessMessageFrontend = ({
	messageTemplateId,
}: {
	messageTemplateId: string;
}): [string, Record<string, never>] =>
	// Returns a [messageTemplateId, stages] tuple — the same shape as FrontendPlacementMessage.
	// The stages object is empty because this message has no frontend rendering.
	// The placement recognises the ID via headlessMessageTemplateIds and will not attempt to render it.
	[messageTemplateId, {}] as [string, Record<string, never>];

export const DefaultHeadlessPlacementBackend = {
	createMessageBackend: <T extends AnyBackendMessageStages>(
		stages: () => BackendMessageStagesToValidated<T>,
	): ReturnType<typeof createPlacementMessageBackend> => createPlacementMessageBackend(stages),
	withDefaultMessageBackend: (): ReturnType<typeof createPlacementMessageBackend> =>
		createPlacementMessageBackend(defaultMessageBackend),
} as const;
export { createPlacementStages } from '../util/runtime';
export { defaultMessageBackend, noValidation } from '../util/message-helpers';
export { useInitialData } from '@post-office/placement-contexts/hooks';
export type {
	AdditionalBackendStages,
	AdditionalFrontendStages,
	CustomPlacementDefinition,
	CustomDefaultFrontend,
	CustomBackend,
	CustomFrontend,
	CustomRenderer,
	CustomTransformMetadata,
	DefaultMessagesResponse,
	RML,
	FrontendStage,
	BackendPlacementStage,
	BackendMessageStage,
} from '../constraints/types';

export type { BackendMessageStageInput, PlacementRestMiddlewareConfig } from '../types';
export type { PlacementRouterMiddlewareFactory } from '../../placement-router/types';
export type {
	BackendMessageBody,
	BackendPlacementMessage,
	FrontendPlacementMessage,
	PlacementBackend,
	PlacementMessageBackendFactory,
	PlacementMessageFrontendFactory,
	PlacementFrontendFactory,
	PlacementBackendFactory,
	RequestContext,
} from '../types';
